import { motion } from "framer-motion";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Zap, Keyboard, FileEdit, CheckSquare, TrendingUp, Clock, Globe, Shield, GraduationCap, DollarSign, Users, Briefcase, ArrowRight, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConsultationForm from "@/components/ConsultationForm";
import TestimonialsSection from "@/components/TestimonialsSection";

const programs = [
  {
    id: "stenou",
    name: "StenoU",
    tagline: "Court Reporting",
    color: "#38b6ff",
    icon: Keyboard,
    description: "Become a certified court reporter. Learn machine shorthand and master realtime transcription at 225+ WPM with 95% accuracy.",
    benefits: [
      { icon: TrendingUp, text: "Average salary $89,000+" },
      { icon: Shield, text: "Stable, in-demand career" },
      { icon: Globe, text: "Work anywhere - remote options" },
      { icon: Clock, text: "Flexible scheduling" }
    ],
    skills: [
      "Machine shorthand (stenography)",
      "Legal & medical terminology",
      "Realtime transcription technology",
      "Court procedures & ethics"
    ],
    career: "Court Reporter / Stenographer"
  },
  {
    id: "editu",
    name: "EditU",
    tagline: "Scopist Training",
    color: "#E8E8E8",
    icon: FileEdit,
    description: "Become a professional scopist. Learn to edit and refine court reporter transcripts, turning raw steno notes into polished legal documents.",
    benefits: [
      { icon: TrendingUp, text: "Earn $25+/hour" },
      { icon: Globe, text: "100% remote work" },
      { icon: Clock, text: "Set your own hours" },
      { icon: Shield, text: "High demand from reporters" }
    ],
    skills: [
      "Transcript editing & formatting",
      "Legal terminology & citation",
      "CAT software proficiency",
      "Grammar & punctuation mastery"
    ],
    career: "Scopist / Transcript Editor"
  },
  {
    id: "proofu",
    name: "ProofU",
    tagline: "Proofreading Excellence",
    color: "#38b6ff",
    icon: CheckSquare,
    description: "Become a transcript proofreader. Learn to catch errors, ensure accuracy, and deliver publication-ready legal transcripts.",
    benefits: [
      { icon: TrendingUp, text: "Earn $15+/hour" },
      { icon: Globe, text: "Work from home" },
      { icon: Clock, text: "Flexible part-time options" },
      { icon: GraduationCap, text: "Fastest path to start" }
    ],
    skills: [
      "Proofreading techniques",
      "Legal document standards",
      "Attention to detail training",
      "Quality assurance processes"
    ],
    career: "Transcript Proofreader"
  }
];

function CTAButton({ className = "" }) {
  return (
    <a href="#consultation">
      <Button
        className={`rounded-lg bg-[#38b6ff] text-white hover:bg-[#38b6ff]/90 btn-neon font-semibold px-8 py-6 text-base ${className}`}
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
        data-testid="cta-get-started"
      >
        Get Started Now <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </a>
  );
}

export default function ProgramsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20">

      {/* ============ HERO SECTION ============ */}
      <section className="py-24 md:py-36 relative overflow-hidden" data-testid="programs-hero">
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-[0%] left-[0%] w-[800px] h-[800px] bg-[#38b6ff]/20 rounded-full blur-[220px]"></div>
        <div className="absolute bottom-[5%] right-[5%] w-[600px] h-[600px] bg-[#1a5aaa]/15 rounded-full blur-[180px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-none text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="programs-heading"
            >
              Become a <span className="gradient-text">Court Reporter</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-xl sm:text-2xl text-[#E0E0E0] italic mb-10"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              The best career you&apos;ve never heard of.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <CTAButton />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ FUNNEL SECTION 1: WHY COURT REPORTING ============ */}
      <section className="py-20 md:py-28 bg-section-dark" data-testid="why-court-reporting">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Opening hook */}
          <div className="text-center mb-16 max-w-4xl mx-auto">
            <p 
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Why Court Reporting?
            </p>
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              A Career That Pays Well, <span className="gradient-text">Keeps You Sharp</span>, and Never Gets Old
            </h2>
            <p className="text-[#E0E0E0] text-lg leading-relaxed">
              Imagine a career where you&apos;re in the room for the most important moments in people&apos;s lives. 
              High-profile trials. High-stakes litigation depositions. Congressional hearings. 
              Court reporters don&apos;t just watch history happen. They create the official record of it.
            </p>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {[
              {
                icon: DollarSign,
                title: "$89,000+",
                subtitle: "Average Annual Salary",
                desc: "Court reporters consistently earn well above the national median. Freelance reporters who develop specialized skills have unlimited earning potential, with top earners commanding premium rates for complex proceedings."
              },
              {
                icon: Users,
                title: "21% Shortage",
                subtitle: "Critical Workforce Gap",
                desc: "There are only 23,000 practicing stenographers left in the country, and that number is shrinking. Courts, law firms, and agencies are competing for qualified reporters. That means job security, leverage, and opportunity for anyone who enters the field now."
              },
              {
                icon: TrendingUp,
                title: "Unlimited Growth",
                subtitle: "Your Skills, Your Future",
                desc: "Unlike jobs that cap out, court reporting rewards you the more skilled you become. Realtime reporters, CART captioners, and broadcast captioners command premium rates. The deeper your specialization, the more you earn."
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-8 text-center"
                data-testid={`why-card-${i + 1}`}
              >
                <div className="w-16 h-16 bg-[#38b6ff]/10 border border-[#38b6ff]/30 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#38b6ff]" />
                </div>
                <h3 
                  className="text-2xl font-bold text-[#F5F5F7] mb-1"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-sm text-[#38b6ff] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {item.subtitle}
                </p>
                <p className="text-[#B0B0B0] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Emotional hook - what it's really like */}
          <div className="max-w-4xl mx-auto mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-10 md:p-14 text-center"
            >
              <p className="text-lg md:text-xl text-[#E0E0E0] leading-relaxed italic mb-6">
                &ldquo;Most people go to work and sit in a cubicle doing the same thing every day. 
                Court reporters walk into a different case every morning. One day you&apos;re in a medical malpractice deposition 
                learning about cutting-edge surgical techniques. The next day you&apos;re covering a patent dispute involving 
                technology that hasn&apos;t hit the market yet. The day after that, you&apos;re in a courtroom watching a jury deliver a verdict 
                that changes someone&apos;s life. No two days are the same, and you never stop learning.&rdquo;
              </p>
              <p className="text-[#38b6ff] text-sm font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                This is what court reporters experience every single week.
              </p>
            </motion.div>
          </div>

          {/* Selling points grid */}
          <div className="mb-20">
            <h3 
              className="text-xl sm:text-2xl font-bold text-[#F5F5F7] text-center mb-12"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              What Makes This Career <span className="gradient-text">Different</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: Briefcase, title: "You Own Your Career", desc: "Court reporters aren't stuck on a corporate ladder waiting for someone to promote them. Freelance reporters choose their own clients, set their own rates, and build their own book of business. Official reporters get government stability with full benefits. Either way, you're in control." },
                { icon: Globe, title: "Flexibility That Actually Means Something", desc: "Need to pick your kids up from school? Want to take a month off to travel? Freelance court reporters have real schedule flexibility, not the fake kind where you still have to ask permission. You work when you want, as much as you want." },
                { icon: Star, title: "You're the Most Important Person in the Room", desc: "Without the court reporter, there is no official record. Attorneys can argue, judges can rule, witnesses can testify, but none of it counts unless you captured it. You are essential to the justice system, and everyone in that room knows it." },
                { icon: GraduationCap, title: "Skip the Student Debt Trap", desc: "A four-year degree costs $100,000+ and doesn't guarantee a job. Court reporting training takes a fraction of the time, costs a fraction of the price, and leads directly to a career where $89,000 is the average, not the ceiling." },
                { icon: Shield, title: "Real Job Security", desc: "Courts aren't going anywhere. Depositions aren't going anywhere. As long as there is a legal system, there will be a need for skilled stenographers who can capture the spoken word with precision and speed. This is a career you can count on for decades." },
                { icon: Zap, title: "Never Stop Growing", desc: "Start as a court reporter. Specialize in realtime. Move into CART captioning for the deaf and hard of hearing. Become a broadcast captioner. Open your own firm. Train the next generation. This isn't a dead-end job; it's a launchpad." }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex gap-5 items-start"
                >
                  <div className="w-12 h-12 bg-[#38b6ff]/10 border border-[#38b6ff]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#38b6ff]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#F5F5F7] mb-2">{item.title}</h3>
                    <p className="text-[#B0B0B0] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Urgency push */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h3 
              className="text-xl sm:text-2xl font-bold text-[#F5F5F7] mb-4"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              The Window Is Open. <span className="gradient-text">Right Now.</span>
            </h3>
            <p className="text-[#E0E0E0] leading-relaxed mb-8">
              With thousands of court reporters retiring and not enough new reporters entering the field, 
              there has never been a better time to start. Law firms are offering signing bonuses. Courts are raising salaries. 
              Agencies are competing for talent. The reporters who get certified now will have their pick of the best opportunities 
              for years to come.
            </p>
          </div>

          <div className="text-center">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* ============ FUNNEL SECTION 2: WHY AYM HIGH STENO ============ */}
      <section className="py-20 md:py-28 bg-section-alt" data-testid="why-ahs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Why Aym High Steno?
              </p>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                We Don&apos;t Just Teach. <span className="gradient-text">We Launch Careers.</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-8 text-lg">
                Aym High Steno is a direct-hire court reporting agency that also trains the next generation 
                of court reporters. Our students learn from working professionals who know exactly 
                what the industry demands, because we hire court reporters every day.
              </p>
              <div className="space-y-5">
                {[
                  "Learn from active court reporting professionals",
                  "Direct path to employment through our agency",
                  "Industry connections from day one",
                  "Programs for every level: StenoU, EditU, ProofU",
                  "Personalized mentorship and support"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#38b6ff] rounded-full flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#E0E0E0]">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { number: "01", title: "Apply & Consult", desc: "Book a free consultation. We'll learn about your goals and recommend the right program for you." },
                { number: "02", title: "Train With Experts", desc: "Dive into our structured curriculum taught by working court reporters and legal professionals." },
                { number: "03", title: "Get Certified", desc: "We prepare you for professional certification with real-world practice and exam preparation." },
                { number: "04", title: "Start Working", desc: "Our agency connections give you a direct pipeline to employment the moment you're ready." }
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card p-6 flex gap-5"
                  data-testid={`step-${i + 1}`}
                >
                  <span 
                    className="text-3xl font-bold text-[#38b6ff]/30"
                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                  >
                    {step.number}
                  </span>
                  <div>
                    <h3 className="font-bold text-[#F5F5F7] mb-1">{step.title}</h3>
                    <p className="text-[#B0B0B0] text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}

              <div className="pt-4">
                <CTAButton className="w-full justify-center" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ PROGRAM DESCRIPTIONS ============ */}
      <section className="py-12 bg-section-dark border-y border-[#1e2430]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {programs.map((program) => (
              <a
                key={program.id}
                href={`#${program.id}`}
                className="flex items-center gap-2 px-6 py-3 bg-[#0D0D0D] border border-[#333333] hover:border-[#38b6ff] transition-colors rounded-lg"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                <program.icon className="w-5 h-5" style={{ color: program.color }} />
                <span className="text-[#F5F5F7] text-sm">{program.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {programs.map((program, index) => (
        <section 
          key={program.id}
          id={program.id}
          className={`py-20 md:py-28 ${index % 2 === 0 ? 'bg-[#0D0D0D]' : 'bg-section-alt'}`}
          data-testid={`program-${program.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-7"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div 
                    className="w-16 h-16 flex items-center justify-center border rounded-xl"
                    style={{ 
                      backgroundColor: `${program.color}10`,
                      borderColor: `${program.color}30`
                    }}
                  >
                    <program.icon className="w-8 h-8" style={{ color: program.color }} />
                  </div>
                  <div>
                    <p 
                      className="text-xs font-bold uppercase tracking-[0.2em] mb-1"
                      style={{ color: program.color, fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {program.tagline}
                    </p>
                    <h2 
                      className="text-3xl sm:text-4xl font-bold tracking-tight text-[#F5F5F7]"
                      style={{ fontFamily: 'Unbounded, sans-serif' }}
                    >
                      {program.name}
                    </h2>
                  </div>
                </div>

                <p className="text-[#E0E0E0] leading-relaxed text-lg mb-8">
                  {program.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {program.benefits.map((benefit, i) => (
                    <div 
                      key={i}
                      className="glass-card flex items-center gap-3 p-4"
                    >
                      <benefit.icon className="w-5 h-5" style={{ color: program.color }} />
                      <span className="text-[#F5F5F7] text-sm">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <CTAButton />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-5"
              >
                <div className="glass-card p-8">
                  <h3 
                    className="text-lg font-bold text-[#F5F5F7] mb-6"
                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                  >
                    What You&apos;ll Learn
                  </h3>
                  <ul className="space-y-4">
                    {program.skills.map((skill, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span 
                          className="w-1.5 h-1.5 rounded-full mt-2"
                          style={{ backgroundColor: program.color }}
                        ></span>
                        <span className="text-[#E0E0E0]">{skill}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* ============ TESTIMONIALS ============ */}
      <TestimonialsSection />

      {/* ============ COMPARISON TABLE ============ */}
      <section className="py-20 md:py-28 bg-section-dark" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p 
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Compare Programs
            </p>
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7]"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              Find Your <span className="gradient-text">Perfect Fit</span>
            </h2>
          </div>

          <div className="glass-card p-6 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b-2 border-[#38b6ff]/40">
                  <th className="text-left py-4 px-4 text-[#F5F5F7] font-normal" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Feature</th>
                  {programs.map((p) => (
                    <th key={p.id} className="text-center py-4 px-4">
                      <span style={{ color: p.color, fontFamily: 'Unbounded, sans-serif' }}>{p.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Training Duration", stenou: "2-3 years", editu: "3-6 months", proofu: "1-3 months" },
                  { feature: "Earning Potential", stenou: "$89,000+/year", editu: "$25+/hour", proofu: "$15+/hour" },
                  { feature: "Work Location", stenou: "Court/Remote", editu: "100% Remote", proofu: "100% Remote" },
                  { feature: "Equipment Needed", stenou: "Steno Machine", editu: "Computer + CAT", proofu: "Computer or Tablet" },
                  { feature: "Certification", stenou: "Required", editu: "Optional", proofu: "Optional" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-[#38b6ff]/20">
                    <td className="py-5 px-4 text-[#FFFFFF] text-sm font-medium">{row.feature}</td>
                    <td className="py-5 px-4 text-center text-[#F5F5F7] text-sm">{row.stenou}</td>
                    <td className="py-5 px-4 text-center text-[#F5F5F7] text-sm">{row.editu}</td>
                    <td className="py-5 px-4 text-center text-[#F5F5F7] text-sm">{row.proofu}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* ============ CONSULTATION FORM ============ */}
      <section id="consultation" className="py-20 md:py-28 bg-section-alt relative overflow-hidden" data-testid="consultation-section">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#38b6ff]/5 rounded-full blur-[150px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <p 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Get Started
              </p>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Book Your <span className="gradient-text">Consultation</span>
              </h2>
              <p className="text-[#E0E0E0] leading-relaxed mb-8">
                The first step to joining any of our programs is a virtual meeting. 
                Book a consultation so we can learn about you, answer your questions, 
                and help you get started on the right path.
              </p>
              <div className="hidden md:block">
                <img
                  src="/stenou-logo.png"
                  alt="StenoU Badge"
                  className="w-32 h-32 object-contain"
                />
              </div>
            </div>
            <div className="md:col-span-7">
              <ConsultationForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
